import { useEffect, useState } from "react";
import { useLocation } from "react-router";

import { loginUrlAfterUnauthorized } from "../app/session-expiry.js";
import { useGo } from "../app/use-go.js";

import { getJson, type FieldErrors } from "./client.js";

/** 読み込みの状態。 */
export type ApiData<T> =
    | { readonly state: "loading" }
    /**
     * status は応答があったときの状態コード（404 なら「見つからない」を出せる）。通信できなければ無い。
     * at は失敗した時刻（断りの文に添える。描き直しても変わらないよう、ここで持つ）。
     * errors は 400 のときの欄ごとの断り（検索の条件の誤り。出すかは画面が決める）。
     */
    | {
        readonly state: "failed";
        readonly status?: number;
        readonly at: Date;
        readonly errors?: FieldErrors | undefined;
    }
    | { readonly state: "ready"; readonly value: T };

/** 401 を受けたときの既定の行き先（戻り先を付けたログイン）。 */
const loginAfterUnauthorized = (returnTo: string): string => loginUrlAfterUnauthorized(returnTo, false);

/**
 * 画面を開いたときに API から読む。
 *
 * <b>ログインしていなければログイン画面へ送る。</b>戻り先に今の画面を付ける
 * （API は未ログインを 401 で返す）。中身の形は呼ぶ側が決める。
 * 401 の行き先を変える画面（招待の承諾。アカウント登録へ送る）は <c>unauthorizedTo</c> を渡す——
 * <b>描くたびに作り直さない関数</b>（モジュールの直下に置いたもの）にする。
 */
export const useApiData = <T>(
    url: string,
    unauthorizedTo: (returnTo: string) => string = loginAfterUnauthorized,
): ApiData<T> => {
    const go = useGo();
    const { pathname, search } = useLocation();
    const [data, setData] = useState<ApiData<T>>({ state: "loading" });

    useEffect(() => {
        let current = true;

        void getJson(url)
            .then((result) => {
                if (!current) {
                    return;
                }
                if (result.ok) {
                    setData({ state: "ready", value: result.value as T });
                } else if (result.status === 401) {
                    // ログインしていたなら、切れたことをログイン画面で知らせる
                    go(unauthorizedTo(pathname + search));
                } else {
                    setData({ state: "failed", status: result.status, at: new Date(), errors: result.errors });
                }
            })
            .catch(() => {
                if (current) {
                    setData({ state: "failed", at: new Date() });
                }
            });

        // 画面を離れた後に返ってきた応答で、次の画面を書き換えない
        return () => {
            current = false;
        };
    }, [url, go, pathname, search, unauthorizedTo]);

    return data;
};
